import { useTranslation } from 'react-i18next'
import WordInList from '@/components/word-in-list.tsx'
import { Info, List, Shapes, SquarePlus, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button.tsx'
import useGlobalStore from '@/useGlobalStore.ts'
import { Sheet } from '@/components/ui/sheet.tsx'
import { useEffect, useState } from 'react'
import useWordDetailsModalStore from '@/useWordDetailsModalStore.ts'
import { cn } from '@/lib/utils.ts'
import { ItemsSheet, WordInListSkeleton } from '@/components/items-sheet.tsx'
import { dispatch, useBus } from '@/useBus.ts'
import { Browser } from '@capacitor/browser'

const PageLibrary = ({ className }: { className: string }) => {
	const { t } = useTranslation()
	const [showAllVocabularySheet, setShowAllVocabularySheet] = useState(false)
	const {
		user,
		libraryOverview,
		allSavedLibraryVocabulary,
		loadAllSavedLibraryVocabulary,
		loadLibraryOverview,
		isLibraryOverviewLoading,
		setIsSheetVisible,
		appLanguageCode,
	} = useGlobalStore()
	const [activeSheet, setActiveSheet] = useState<string>('')
	const { setMode, showModal, setOnSuccessfulFinalAction } = useWordDetailsModalStore()

	const openAllVocabulariesSheet = async () => {
		await loadAllSavedLibraryVocabulary()
		setShowAllVocabularySheet(true)
		await setActiveSheetHandler('vocabularies')
	}

	const setActiveSheetHandler = async (sheet: string) => {
		setActiveSheet(sheet)
		if (allSavedLibraryVocabulary === null) {
			await loadAllSavedLibraryVocabulary()
		}
	}

	const showAddWordModal = () => {
		setOnSuccessfulFinalAction(async () => {
			await loadLibraryOverview()
			await loadAllSavedLibraryVocabulary()
		})
		setMode('add')
		showModal()
	}

	const openImportExportUserDataPage = async () => {
		if (user) {
			const finishedHandle = await Browser.addListener('browserFinished', async () => {
				await loadLibraryOverview()
				await loadAllSavedLibraryVocabulary()

				await finishedHandle.remove()
			})

			await Browser.open({
				url: `${import.meta.env.VITE_APP_URL}/app/gateway?redirect=profile.user-data&app_language_code=${appLanguageCode}&email=${user.email}&auth=${user.access_token}`,
				presentationStyle: 'popover',
			})
		}
	}

	useBus('close-sheet', () => {
		setShowAllVocabularySheet(false)
	})

	useBus('reload-open-collection', async () => {
		await loadAllSavedLibraryVocabulary()
	})

	useEffect(() => {
		setIsSheetVisible(showAllVocabularySheet)
	}, [setIsSheetVisible, showAllVocabularySheet])

	return (
		<div className={cn(className, 'overflow-y-scroll pb-2')}>
			<Sheet open={showAllVocabularySheet} onOpenChange={setShowAllVocabularySheet}>
				<div className="flex flex-col justify-between">
					<div className="flex flex-row -mb-2">
						<Button
							onClick={() => dispatch('change-page', { page: 5 })}
							variant="ghost"
							className="flex items-center justify-start rounded-none p-2 w-full">
							<Shapes className="h-5 w-5" />
							<h6 className="m-2">{t('See collections')}</h6>
						</Button>
					</div>
					<div className="flex flex-row -mb-2">
						<Button
							onClick={() => openImportExportUserDataPage()}
							variant="ghost"
							className="flex items-center justify-start rounded-none p-2 w-full">
							<Upload className="h-5 w-5" />
							<h6 className="m-2">{t('Import / Export data')}</h6>
						</Button>
					</div>
					<div className="flex flex-row">
						<Button
							onClick={openAllVocabulariesSheet}
							variant="ghost"
							className="flex items-center justify-start rounded-none p-2 w-full">
							<List className="h-5 w-5" />
							<h6 className="m-2">{t('Show all saved vocabulary')}</h6>
						</Button>
						<Button onClick={showAddWordModal} variant="ghost" size="icon">
							<SquarePlus className="h-5 w-5" />
						</Button>
					</div>
				</div>
				<div className="flex">
					<div className="flex flex-none m-2 w-11 text-sm justify-center">{t('Status')}</div>
					<div className="grow m-2 text-sm">{t('Word / Term')}</div>
				</div>
				<div>
					{isLibraryOverviewLoading && (libraryOverview?.vocabulary ?? []).length <= 0 ? (
						[1, 2, 3, 4, 5, 6, 7, 8].map((x) => <WordInListSkeleton key={x} mode="summary" />)
					) : libraryOverview?.vocabulary !== undefined && libraryOverview?.vocabulary.length <= 0 ? (
						<div className="w-full gap-3 flex flex-col items-center justify-center mt-3 font-medium text-sm">
							<Info className="w-5 h-5" /> {t('Your library is empty')}
						</div>
					) : (
						libraryOverview?.vocabulary.map((libVoc, idx) => (
							<WordInList key={idx} index={idx} libraryVocabulary={libVoc} mode="summary" />
						))
					)}
					{libraryOverview?.hasMoreVocabulary && (
						<div
							onClick={openAllVocabulariesSheet}
							className="flex justify-center items-center w-full h-8 cursor-pointer uppercase text-xs tracking-wider rounded-b-md shadow-md select-none">
							{t('View All')}
						</div>
					)}
				</div>
				{activeSheet === 'vocabularies' && (
					<ItemsSheet
						onClose={() => setShowAllVocabularySheet(false)}
						vocabularyList={allSavedLibraryVocabulary}
					/>
				)}
			</Sheet>
		</div>
	)
}

export default PageLibrary
